import { GoogleMap, Marker, useJsApiLoader, OverlayView } from "@react-google-maps/api";
import { GatewayData } from "../types";
import { useState, useEffect, useCallback } from "react";

interface GoogleMapLocationProps {
  gatewayData: GatewayData[];
  mapCenter?: { lat: number; lng: number };
  mapZoom?: number;
  mapApiKey?: string;
  mapWidth?: string;
  mapHeight?: string;
  onGatewayClick?: (gateway: GatewayData) => void;
  lastPageUpdate?: Date;
  selectedDtu?: string; // Add this prop
}

// Style the map with user-friendly controls and interactions
const mapOptions = {
  disableDefaultUI: false, // Enable default controls
  zoomControl: true, // Enable zoom controls
  mapTypeControl: true, // Enable map type controls (satellite, terrain, etc.)
  streetViewControl: false, // Keep street view disabled
  fullscreenControl: true, // Enable fullscreen control
  scrollwheel: true, // Enable zoom on scroll
  gestureHandling: "auto", // Enable all gestures (panning, zooming)
  draggable: true, // Enable map dragging
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }], // Hide points of interest labels
    },
  ],
};

const GoogleMapLocationGoogle: React.FC<GoogleMapLocationProps> = ({
  gatewayData: gatewayData,
  mapCenter,
  mapZoom,
  mapApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
  mapWidth = "100%",
  mapHeight = "100%",
  onGatewayClick,
  lastPageUpdate,
  selectedDtu,
}) => {
  const [selectedGateway, setSelectedGateway] = useState<GatewayData | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [userHasMovedMap, setUserHasMovedMap] = useState<boolean>(false);
  const [currentCenter, setCurrentCenter] = useState(mapCenter);

  // Load Google Maps API with useJsApiLoader hook
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
  });

  // Handle map drag/pan to detect user interaction
  const handleMapDragEnd = useCallback(() => {
    setUserHasMovedMap(true);
  }, []);

  // Reset user interaction when DTU selection changes
  useEffect(() => {
    setUserHasMovedMap(false);
    setCurrentCenter(mapCenter);
  }, [selectedDtu]);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Check on resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const mapContainerStyle = {
    width: mapWidth,
    height: mapHeight,
    position: "relative" as const,
  };

  // check gateway's lng and lat are defined
  const checkGatewayCoordinates = (gateway: GatewayData) => {
    if (gateway.lat === undefined || gateway.lng === undefined) {
      throw new Error(`No coordinates found for ${gateway.alias}`);
    }
  };

  // Get marker position using device coordinates or fallback to map center
  const getMarkerPosition = (gateway: GatewayData) => {
    checkGatewayCoordinates(gateway);

    return { lat: gateway.lat, lng: gateway.lng };
  };

  const getGatewayStatusCounts = (gateway: GatewayData) => {
    const maxSensors = 10;
    let normalCount = 0;
    let alertCount = 0;
    let brokenCount = 0;

    const statusArray = Array.isArray(gateway.nodes_status) ? gateway.nodes_status : [];
    const nodeArray = Array.isArray(gateway.nodes) ? gateway.nodes : [];

    for (let index = 0; index < Math.min(maxSensors, statusArray.length); index += 1) {
      const status = statusArray[index];
      if (status === 0) {
        brokenCount += 1;
        continue;
      }

      const value = nodeArray[index];
      if (value === 0) {
        alertCount += 1;
      } else if (value !== undefined) {
        normalCount += 1;
      }
    }

    return { normalCount, alertCount, brokenCount };
  };

  const getGatewayStatusPercentages = (gateway: GatewayData) => {
    const { normalCount, alertCount, brokenCount } = getGatewayStatusCounts(gateway);
    const total = normalCount + alertCount + brokenCount;

    if (total === 0) {
      return { normalPct: 100, alertPct: 0, brokenPct: 0 };
    }

    return {
      normalPct: (normalCount / total) * 100,
      alertPct: (alertCount / total) * 100,
      brokenPct: (brokenCount / total) * 100,
    };
  };

  const createGradientMarkerUrl = (normalPct: number, alertPct: number, brokenPct: number) => {
    const stopValues = [
      { offset: 0, color: "#22c55e" },
      { offset: normalPct, color: "#22c55e" },
      { offset: normalPct, color: "#ef4444" },
      { offset: normalPct + alertPct, color: "#ef4444" },
      { offset: normalPct + alertPct, color: "#f59e0b" },
      { offset: 100, color: "#f59e0b" },
    ];

    const stops = stopValues
      .map((stop) => `<stop offset="${stop.offset}%" stop-color="${stop.color}"/>`)
      .join("");

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="60" viewBox="0 0 50 60">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
            ${stops}
          </linearGradient>
        </defs>
        <path
          d="M25 2
             C14 2, 6 11, 6 22
             C6 36, 25 56, 25 56
             C25 56, 44 36, 44 22
             C44 11, 36 2, 25 2
             Z"
          fill="url(#grad)"
          stroke="#333"
          stroke-width="1"
        />
        <circle cx="25" cy="20" r="8" fill="rgba(255,255,255,0.5)" />
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  const createSolidPinMarkerUrl = (fillColor: string) => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="60" viewBox="0 0 50 60">
        <path
          d="M25 2
             C14 2, 6 11, 6 22
             C6 36, 25 56, 25 56
             C25 56, 44 36, 44 22
             C44 11, 36 2, 25 2
             Z"
          fill="${fillColor}"
          stroke="#333"
          stroke-width="1"
        />
        <circle cx="25" cy="20" r="8" fill="rgba(255,255,255,0.5)" />
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  // Determine if a DTU is disconnected (offline for 24+ hours)
  const isDtuDisconnected = (gateway: GatewayData): boolean => {
    const lastUpdate = new Date(gateway.timestamp || gateway.time || 0);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
    return hoursDiff >= 24;
  };

  // Handle marker click event
  const handleMarkerClick = (gateway: GatewayData) => {
    setSelectedGateway(gateway);
    if (onGatewayClick) {
      onGatewayClick(gateway);
    }
  };

  // Format date as YYYY/MM/DD HH:mm:ss
  const formatDate = (date: Date | string | number): string => {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    return `${dateObj.getFullYear()}/${(dateObj.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${dateObj.getDate().toString().padStart(2, "0")} ${dateObj
      .getHours()
      .toString()
      .padStart(2, "0")}:${dateObj.getMinutes().toString().padStart(2, "0")}:${dateObj.getSeconds().toString().padStart(2, "0")}`;
  };



  // Show error message if Maps API fails to load
  if (loadError) {
    return <div className="flex items-center justify-center h-full bg-gray-100 text-red-500">Error loading maps</div>;
  }

  // Show loading message while Maps API is loading
  if (!isLoaded) {
    return <div className="flex items-center justify-center h-full bg-gray-100">Loading maps...</div>;
  }

  return (
    <div style={{ position: "relative", width: mapWidth, height: mapHeight }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={currentCenter}
        zoom={mapZoom}
        options={mapOptions}
        onDragEnd={handleMapDragEnd}
      >
        {/* Map markers - simplified implementation without custom icons */}
        {gatewayData.map((gateway) => {
          const position = getMarkerPosition(gateway);
          const isDisconnected = isDtuDisconnected(gateway);

          const { normalPct, alertPct, brokenPct } = getGatewayStatusPercentages(gateway);
          const gradientIconUrl = createGradientMarkerUrl(normalPct, alertPct, brokenPct);

          return (
            <Marker
              key={gateway._id}
              position={position}
              onClick={() => handleMarkerClick(gateway)}
              title={`${gateway.alias} - Click for details`}
              label={{
                text: gateway.alias || '',
                color: "black",
                fontWeight: "bold",
                fontSize: isMobile ? "10px" : "15px",
                className: "marker-label"
              }}
              icon={
                isDisconnected
                  ? {
                      url: createSolidPinMarkerUrl("#000000"),
                      scaledSize: isLoaded ? new google.maps.Size(isMobile ? 25 : 35, isMobile ? 35 : 50) : undefined,
                      labelOrigin: new google.maps.Point(
                        /* x = half of scaled width  → centers horizontally */
                        isMobile ? 12.5 : 17.5,
                        
                        /* y = more than height  → places label below the icon */
                        isMobile ? 37.5 : 47.5
                      )
                    }
                  : {
                      url: gradientIconUrl,
                      scaledSize: isLoaded ? new google.maps.Size(isMobile ? 25 : 35, isMobile ? 35 : 50) : undefined,
                      labelOrigin: new google.maps.Point(
                        isMobile ? 12.5 : 17.5,
                        isMobile ? 37.5 : 47.5
                      )
                    }
              }
            />
          );
        })}

        {/* Info Window for selected gateway */}
        {selectedGateway && (
          <OverlayView
            position={getMarkerPosition(selectedGateway)}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div className="bg-white rounded shadow-lg p-4 border relative" style={{ 
              width: isMobile ? '220px' : '320px',
              maxWidth: '90vw',
              transform: 'translate(-50%, -100%)',
              marginTop: '-10px',
              minHeight: isMobile ? '200px' : '250px'
            }}>
              {/* Arrow pointing down */}
              <div 
                className="absolute left-1/2 transform -translate-x-1/2"
                style={{
                  bottom: '-12px',
                  width: 0,
                  height: 0,
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '12px solid white',
                  filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
                }}
              />

              <button 
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                onClick={() => setSelectedGateway(null)}
              >
                x
              </button>
              <div className="flex justify-between items-center mb-2">
                <div className={`${isMobile ? 'text-base' : 'text-xl'} font-bold truncate`}>{selectedGateway.alias}</div>
              </div>

              <div className={`${isMobile ? 'text-sm' : 'text-base'} mb-3`}>
                Last Online: {lastPageUpdate ? formatDate(new Date(selectedGateway.time || selectedGateway.timestamp || new Date())) : formatDate(lastPageUpdate)}
              </div>

              <div className="border-t border-gray-300 my-3"></div>

              <div className="grid grid-cols-2 gap-2"> {/* Increased gap */}
                {Array(5).fill(0).map((_, i) => {
                  const index = i;
                  const isWorking = selectedGateway.nodes_status && 
                    Array.isArray(selectedGateway.nodes_status) && 
                    selectedGateway.nodes_status.length > index && 
                    selectedGateway.nodes_status[index] !== 0;

                  let status;
                  if (!isWorking) {
                    status = "Malfunction";
                  } else if (selectedGateway.nodes && selectedGateway.nodes[index] !== 0) {
                    status = "Normal";
                  } else {
                    status = "Low Level";
                  }

                  // Get sensor online time
                  const sensorOnlineTime = selectedGateway.sensor_online_time && 
                    Array.isArray(selectedGateway.sensor_online_time) && 
                    selectedGateway.sensor_online_time.length > index 
                    ? formatDate(selectedGateway.sensor_online_time[index])
                    : "No data";

                  return (
                    <div key={`left-${index}`} className={`${isMobile ? 'text-xs' : 'text-sm'} px-1`}>
                      <div>
                        S{index + 1}: <span className={
                          !isWorking ? "text-yellow-500" :
                          selectedGateway.nodes && selectedGateway.nodes[index] !== 0 ? "text-green-500" : "text-red-500"
                        }>{status}</span>
                      </div>
                      <div className="text-xs text-gray-500">{sensorOnlineTime}</div>
                    </div>
                  );
                })}

                {Array(5).fill(0).map((_, i) => {
                  const index = i + 5;
                  const isWorking = selectedGateway.nodes_status && 
                    Array.isArray(selectedGateway.nodes_status) && 
                    selectedGateway.nodes_status.length > index && 
                    selectedGateway.nodes_status[index] !== 0;

                  let status;
                  if (!isWorking) {
                    status = "Malfunction";
                  } else if (selectedGateway.nodes && selectedGateway.nodes[index] !== 0) {
                    status = "Normal";
                  } else {
                    status = "Low Level";
                  }

                  // Get sensor online time
                  const sensorOnlineTime = selectedGateway.sensor_online_time && 
                    Array.isArray(selectedGateway.sensor_online_time) && 
                    selectedGateway.sensor_online_time.length > index 
                    ? formatDate(selectedGateway.sensor_online_time[index])
                    : "No data";

                  return (
                    <div key={`right-${index}`} className={`${isMobile ? 'text-xs' : 'text-sm'} px-1`}>
                      <div>
                        S{index + 1}: <span className={
                          !isWorking ? "text-yellow-500" :
                          selectedGateway.nodes && selectedGateway.nodes[index] !== 0 ? "text-green-500" : "text-red-500"
                        }>{status}</span>
                      </div>
                      <div className="text-xs text-gray-500">{sensorOnlineTime}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </OverlayView>
        )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapLocationGoogle;
